import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Package, MapPin, Building2, Camera } from "lucide-react";
import { StatusPill, PrimaryButton } from "@/components/brand";
import { ProofOfDelivery } from "@/components/ProofOfDelivery";
import { SignaturePad } from "@/components/SignaturePad";
import MapView from "@/components/MapView";
import api from "@/lib/api";

const NEXT_ACTIONS = {
  assigned: [{ status: "picked_up", label: "Mark picked up" }],
  picked_up: [{ status: "en_route", label: "Mark en route" }],
  en_route: [],
};

export default function DeliveryDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [signature, setSignature] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [issue, setIssue] = useState("");
  const [showIssue, setShowIssue] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/deliveries/${id}`);
    setD(data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const tracking = d && ["assigned", "picked_up", "en_route"].includes(d.status);
  const [geoError, setGeoError] = useState(false);
  useEffect(() => {
    if (!tracking || !navigator.geolocation) return;
    const send = (p) => api.patch(`/deliveries/${id}/location`, { lat: p.coords.latitude, lng: p.coords.longitude }).catch(() => {});
    const w = navigator.geolocation.watchPosition(send, () => setGeoError(true), { enableHighAccuracy: true, maximumAge: 5000 });
    return () => navigator.geolocation.clearWatch(w);
  }, [tracking, id]);

  const update = async (status, note = "") => {
    setBusy(true);
    try { await api.patch(`/deliveries/${id}/status`, { status, note }); await load(); } finally { setBusy(false); }
  };

  const deliverWithPhoto = async () => {
    if (!photo) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", photo);
      if (signature) fd.append("signature", signature, "signature.png");
      fd.append("recipient_name", recipient);
      await api.post(`/deliveries/${id}/pod`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch(`/deliveries/${id}/status`, { status: "delivered", note: recipient ? `Signed for by ${recipient}` : "Photo proof captured" });
      setPhoto(null); setSignature(null); setRecipient("");
      await load();
    } finally { setBusy(false); }
  };

  const submitIssue = async () => {
    await update("exception", issue.trim() || "Flagged by driver");
    setIssue(""); setShowIssue(false);
  };

  if (!d) return <div className="text-sm text-gray-400 font-mono uppercase tracking-wide">Loading…</div>;

  const actions = NEXT_ACTIONS[d.status] || [];
  const canException = ["assigned", "picked_up", "en_route"].includes(d.status);
  const points = [
    { lat: d.pickup.lat, lng: d.pickup.lng, label: `Pickup · ${d.pickup.address}`, type: "pickup" },
    { lat: d.dropoff.lat, lng: d.dropoff.lng, label: `Dropoff · ${d.dropoff.address}`, type: "dropoff" },
  ];

  return (
    <div data-testid="delivery-detail">
      <Link to="/driver/deliveries" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#0a0a0a] mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to deliveries
      </Link>

      <div className="flex items-center gap-4 mb-2">
        <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">DISPATCH · {d.id.slice(0, 8).toUpperCase()}</span>
        <StatusPill status={d.status} />
      </div>
      <h1 className="text-2xl font-bold tracking-tight text-[#0a0a0a] mb-8">{d.package_description}</h1>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <MapView points={points} route={points} height={340} />

          <div className="grid sm:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
            <div className="bg-white p-5">
              <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400 mb-2"><MapPin className="h-3.5 w-3.5" /> Pickup</div>
              <div className="text-sm text-[#0a0a0a]">{d.pickup.address}</div>
              {d.pickup.contact && <div className="text-xs text-gray-500 mt-1">Contact · {d.pickup.contact}</div>}
            </div>
            <div className="bg-white p-5">
              <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-orange-600 mb-2"><MapPin className="h-3.5 w-3.5" /> Dropoff</div>
              <div className="text-sm text-[#0a0a0a]">{d.dropoff.address}</div>
              {d.dropoff.contact && <div className="text-xs text-gray-500 mt-1">Contact · {d.dropoff.contact}</div>}
            </div>
          </div>

          <div className="border border-gray-200 p-5">
            <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400 mb-3"><Building2 className="h-3.5 w-3.5" /> Merchant</div>
            <div className="text-sm text-[#0a0a0a]">{d.merchant_name}</div>
            <div className="mt-3 flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400"><Package className="h-3.5 w-3.5" /> Package notes</div>
            <div className="text-sm text-gray-600 mt-1">{d.package_notes || "—"}</div>
          </div>
        </div>

        {/* Actions + timeline */}
        <div className="space-y-6">
          <div className="border border-gray-200 p-5">
            <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400 mb-4">Update status</div>
            {tracking && (
              <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.14em] uppercase text-gray-400 mb-3" data-testid="driver-location-sharing">
                <span className={`h-2 w-2 rounded-full ${geoError ? "bg-gray-300" : "bg-orange-500 animate-pulse"}`} />
                {geoError ? "Location off · enable GPS to share live position" : "Sharing live location with merchant"}
              </div>
            )}
            <div className="space-y-2">
              {actions.map((a) => (
                <PrimaryButton key={a.status} orange data-testid={`status-action-${a.status}`} disabled={busy} onClick={() => update(a.status)} className="w-full">
                  {a.label}
                </PrimaryButton>
              ))}
              {d.status === "en_route" && (
                <div className="space-y-2" data-testid="pod-capture">
                  <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
                    data-testid="pod-file-input" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
                  <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="pod-capture-button"
                    className="w-full px-6 py-3 text-sm font-semibold border border-[#0a0a0a] text-[#0a0a0a] hover:bg-gray-50 transition-colors inline-flex items-center justify-center gap-2">
                    <Camera className="h-4 w-4" /> {photo ? "Retake photo" : "Take dropoff photo"}
                  </button>
                  {photo && <img src={URL.createObjectURL(photo)} alt="Preview" className="w-full max-h-48 object-cover border border-gray-200" data-testid="pod-preview" />}
                  <input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="Recipient name (optional)" maxLength={80}
                    className="w-full text-sm border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#0a0a0a]" data-testid="recipient-name-input" />
                  <SignaturePad onChange={setSignature} />
                  <PrimaryButton orange className="w-full" disabled={busy || !photo} onClick={deliverWithPhoto} data-testid="status-action-delivered">
                    Mark delivered
                  </PrimaryButton>
                  {!photo && <p className="text-xs text-gray-400">A dropoff photo is required to complete this delivery.</p>}
                </div>
              )}
              {canException && !showIssue && (
                <button
                  data-testid="status-action-exception"
                  disabled={busy}
                  onClick={() => setShowIssue(true)}
                  className="w-full px-6 py-3 text-sm font-semibold border border-orange-500 text-orange-600 hover:bg-orange-50 transition-colors"
                >
                  Report exception
                </button>
              )}
              {showIssue && (
                <div className="space-y-2 border border-orange-500 p-3" data-testid="exception-form">
                  <textarea value={issue} onChange={(e) => setIssue(e.target.value)} rows={3} placeholder="What went wrong? (recipient unavailable, wrong address…)"
                    className="w-full text-sm border border-gray-200 p-2 focus:outline-none focus:border-[#0a0a0a]" data-testid="exception-note-input" />
                  <div className="flex gap-2">
                    <button onClick={submitIssue} disabled={busy} data-testid="exception-submit-button"
                      className="flex-1 px-4 py-2 text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors">Send alert</button>
                    <button onClick={() => setShowIssue(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-[#0a0a0a]" data-testid="exception-cancel-button">Cancel</button>
                  </div>
                </div>
              )}
              {!actions.length && !canException && <div className="text-sm text-gray-400">No actions available.</div>}
            </div>
          </div>

          <ProofOfDelivery delivery={d} />

          <div className="border border-gray-200 p-5">
            <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400 mb-4">Timeline</div>
            <ol className="space-y-4" data-testid="delivery-timeline">
              {d.timeline.map((t, i) => (
                <li key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <span className={`h-2.5 w-2.5 rounded-full ${i === d.timeline.length - 1 ? "bg-orange-500" : "bg-[#0a0a0a]"}`} />
                    {i < d.timeline.length - 1 && <span className="w-px flex-1 bg-gray-200 my-1" />}
                  </div>
                  <div className="pb-1">
                    <div className="text-sm text-[#0a0a0a] capitalize">{t.status.replace("_", " ")}</div>
                    <div className="font-mono text-[10px] text-gray-400">{new Date(t.at).toLocaleString()}</div>
                    {t.note && <div className="text-xs text-gray-500 mt-0.5">{t.note}</div>}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
